import { useEffect, useState } from 'react';
import type { RoomShapeBackground } from '@peles/shared';
import { createEntityAttachment, getAttachmentUploadUrl } from '../../../api/entity-attachments';
import { uploadFileToS3 } from '../../../api/photos';
import { useEntityAttachments } from '../../../hooks/useEntityAttachments';
import { isPdfFile, pdfToImageFiles } from '../../../utils/pdfToImages';
import type { CanvasBackground } from './RoomShapeCanvas';

/** Attachments of a room are filed under this entity type. */
export const ROOM_ATTACHMENT_ENTITY = 'ROOM';

/** An image's size in pixels, once the browser has decoded it. */
export function loadImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error('The drawing could not be opened.'));
    img.src = url;
  });
}

/**
 * The drawing a room is traced over: which file it is, where to fetch it and
 * how big it is. Uploading files it as an attachment on the room, so the
 * drawing is there the next time the shape is opened.
 */
export function useTraceDrawing(
  roomId: string,
  background: RoomShapeBackground | null | undefined,
) {
  const attachments = useEntityAttachments(ROOM_ATTACHMENT_ENTITY, roomId);
  // A drawing just uploaded is shown from the file itself, before the list refetches.
  const [local, setLocal] = useState<{ attachmentId: string; url: string } | null>(null);
  const [size, setSize] = useState<{ url: string; width: number; height: number } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const url = background
    ? local?.attachmentId === background.attachmentId
      ? local.url
      : (attachments.data?.find((a) => a.id === background.attachmentId)?.downloadUrl ?? null)
    : null;

  useEffect(() => {
    if (!url || size?.url === url) return;
    let live = true;
    loadImageSize(url)
      .then((s) => live && setSize({ url, ...s }))
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [url, size?.url]);

  useEffect(
    () => () => {
      if (local) URL.revokeObjectURL(local.url);
    },
    [local],
  );

  /** Upload a drawing; resolves to its attachment id and pixel size. */
  const upload = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      // A PDF plan is traced from its first page.
      const image = isPdfFile(file) ? (await pdfToImageFiles(file, 2))[0] : file;
      if (!image) throw new Error('That PDF has no pages.');
      const { uploadUrl, s3Key } = await getAttachmentUploadUrl(image.name, image.type);
      await uploadFileToS3(uploadUrl, image);
      const attachment = await createEntityAttachment({
        entityType: ROOM_ATTACHMENT_ENTITY,
        entityId: roomId,
        fileName: image.name,
        s3Key,
        mimeType: image.type,
        fileSizeBytes: image.size,
      });
      const objectUrl = URL.createObjectURL(image);
      const dims = await loadImageSize(objectUrl);
      setLocal({ attachmentId: attachment.id, url: objectUrl });
      setSize({ url: objectUrl, ...dims });
      return { attachmentId: attachment.id, ...dims };
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The drawing could not be uploaded.');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const canvasBackground: CanvasBackground | null =
    background && url && size?.url === url
      ? {
          url,
          x: background.x,
          y: background.y,
          feetPerPixel: background.feetPerPixel,
          opacity: background.opacity ?? 0.6,
          pixelWidth: size.width,
          pixelHeight: size.height,
        }
      : null;

  return {
    canvasBackground,
    loading: !!background && !canvasBackground && !error,
    uploading,
    error,
    upload,
  };
}
